import { useEffect, useMemo, useState } from 'react';
import type { Challenge } from '../data/types';
import { PER_CHAPTER, buildsOf, kindOf, type Chapter } from '../data/road';
import { slotsFor } from '../data/film';
import { ExerciseArt } from './ExerciseArt';
import type { StopPrint } from '../lib/today-waiting';
import { printOf } from '../lib/depth-road';

/**
 * The road, drawn (K41 RD1, owner 2026-09-28): one chapter of up to seven stops
 * winding up painted hills, its lesson on the gate at the top, and the person's
 * goal on the far hill. It is a game map with words on it: every stop says what
 * it is, what kind of making, how long, and what it builds.
 *
 * The hills are one painting (public/art/road/road-bg.jpg). The path, the stops
 * and every word are SVG and HTML over it, so they sit exactly where the data
 * says and the words stay real text. Tapping a stop opens its signpost (RD3);
 * the signpost's Start is the only way in, so a stray tap never starts work.
 */

/** Map units: 100 wide, H tall. Stops climb from the bottom, alternating sides. */
const H = 180;
const STOP_Y = (i: number) => 169 - i * 18.2;
const STOP_X = (i: number) => (i % 2 === 0 ? 26 : 74);
const GATE_Y = 38;
const GOAL_Y = 11;

/** A smooth line through the points (Catmull-Rom as cubic Béziers). */
function smooth(pts: [number, number][]): string {
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C ${c1[0]} ${c1[1]}, ${c2[0]} ${c2[1]}, ${p2[0]} ${p2[1]}`;
  }
  return d;
}

const pct = (v: number, of: number) => `${(v / of) * 100}%`;

/**
 * A done stop's picture goes through the darkroom like every print (K37–K39):
 * developing while the crew work, dry and face-down until you turn it over,
 * then the picture itself with its ✓.
 */
export interface RoadStop { c: Challenge; state: 'done' | 'next' | 'later'; day?: string; picture?: string; print?: StopPrint }


export function RoadMap({ chapter, stops, goal, landed, onStart, onTurn }: {
  chapter: Chapter;
  stops: RoadStop[];
  goal: { line: string; art: string };
  /** the stop just finished, when you arrive from the reward: it pops as its picture lands */
  landed?: string;
  onStart: (c: Challenge, how: 'next' | 'again' | 'pick') => void;
  /** a dry print's stop: straight to turning it over, no signpost in the way */
  onTurn: (s: RoadStop) => void;
}) {
  const [open, setOpen] = useState<RoadStop | null>(null);
  // one lit next step: on a short phone, scroll just far enough that it (and the stop that just got its picture) is on screen
  useEffect(() => {
    const t = setTimeout(() => {
      document.querySelector('.road-stop.just-landed')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      document.querySelector('.road-stop.next')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 250);
    return () => clearTimeout(t);
  }, [landed]);
  const pts = useMemo(() => {
    const p: [number, number][] = [[50, H + 4]];
    stops.forEach((_, i) => p.push([STOP_X(i), STOP_Y(i)]));
    p.push([50, GATE_Y + 4]);
    return p;
  }, [stops]);
  const d = smooth(pts);
  const beyond = smooth([[50, GATE_Y + 4], [50, GATE_Y - 8], [56, GOAL_Y + 16], [50, GOAL_Y + 8]]);

  return (
    <div className="roadmap" style={{ aspectRatio: `100 / ${H}` }}>
      <img className="roadmap-bg" src="/art/road/road-bg.jpg" alt="" draggable={false} />
      <svg className="roadmap-path" viewBox={`0 0 100 ${H}`} preserveAspectRatio="none" aria-hidden>
        <path d={beyond} className="path-far" />
        <path d={d} className="path-edge" />
        <path d={d} className="path-way" />
      </svg>

      <div className="road-goal" style={{ top: pct(GOAL_Y, H) }}>
        <img src={goal.art} alt={goal.line} draggable={false} />
      </div>

      <div className="road-gate" style={{ top: pct(GATE_Y, H) }}>
        <img src="/art/road/gate.webp" alt="" draggable={false} />
        <span className="road-gate-banner"><small>Chapter {chapter.n}</small>{chapter.lesson}</span>
      </div>

      {stops.map((s, i) => {
        const x = STOP_X(i);
        const y = STOP_Y(i);
        const kind = kindOf(s.c);
        const builds = buildsOf(s.c);
        const left = x < 50;
        const print = printOf(s);
        return (
          <div key={s.c.id}>
            <button
              type="button"
              className={`road-stop ${s.state} ${print ?? ''} ${landed === s.c.id ? 'just-landed' : ''}`}
              style={{ left: `${x}%`, top: pct(y, H) }}
              onClick={() => (print === 'dry' ? onTurn(s) : setOpen(s))}
              aria-label={`${s.c.title}${print === 'dry' ? ', a print is dry: turn it over' : print === 'developing' ? ', your crew are developing it' : s.state === 'done' ? ', done' : s.state === 'next' ? ', next' : ''}`}
            >
              {print === 'dry' ? <span className="road-print-back" />
                : print === 'developing' ? <><ExerciseArt id={s.c.id} className="road-stop-art" /><span className="road-safelight"><span className="dot pulse" /><span className="dot pulse d2" /><span className="dot pulse d3" /></span></>
                : s.state === 'done'
                ? s.picture ? <img src={s.picture} alt="" /> : <ExerciseArt id={s.c.id} className="road-stop-art" />
                : <span className="road-stop-n">{i + 1 + (chapter.n - 1) * PER_CHAPTER}</span>}
              {s.state === 'done' && !print && <span className="road-tick">✓</span>}
              {print === 'dry' && <span className="tap-ring" />}
              {s.state === 'next' && <span className="tap-ring" />}
            </button>
            {s.state === 'next' && <img className="road-kraben" src="/art/road/kraben-wave.webp" alt="" style={{ left: `${x}%`, top: pct(y, H) }} draggable={false} />}
            <button type="button" className={`road-label ${s.state} ${left ? 'right-of' : 'left-of'}`} style={{ top: pct(y, H), ...(left ? { left: `${x + 10.5}%` } : { right: `${100 - x + 10.5}%` }) }} onClick={() => setOpen(s)} tabIndex={-1}>
              <b>{s.c.title}</b>
              <span className="road-meta"><span className={`kind ${kind.tone}`}>{kind.label}</span>{s.c.minutes} min{s.state === 'done' && s.day ? ` · Day ${s.day}` : ''}</span>
              {print === 'dry' ? <span className="road-builds road-print-line dry">A print is dry · turn it over</span>
                : print === 'developing' ? <span className="road-builds road-print-line">Your crew are developing it…</span>
                : builds && <span className="road-builds">{builds}</span>}
            </button>
          </div>
        );
      })}

      {open && <Signpost stop={open} n={stops.indexOf(open) + 1 + (chapter.n - 1) * PER_CHAPTER} onClose={() => setOpen(null)} onStart={onStart} />}
    </div>
  );
}

/** A tapped stop (K41 RD3): what you'll do, what kind, what it builds, what it gives your film. */
export function Signpost({ stop, n, onClose, onStart }: { stop: RoadStop; n: number; onClose: () => void; onStart: (c: Challenge, how: 'next' | 'again' | 'pick') => void }) {
  const { c } = stop;
  const kind = kindOf(c);
  const builds = buildsOf(c);
  const gives = slotsFor(c)[0];
  return (
    <div className="signpost-scrim" onClick={onClose}>
      <div className="signpost" role="dialog" aria-label={c.title} onClick={(e) => e.stopPropagation()}>
        <div className="signpost-art">
          {stop.state === 'done' && stop.picture && !printOf(stop) ? <img src={stop.picture} alt="" /> : <ExerciseArt id={c.id} />}
        </div>
        <h2>{c.title}</h2>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
          <span className={`kind big ${kind.tone}`}>{kind.label}</span>
          <span className="pill">{c.minutes} min</span>
          <span className="pill">{stop.state === 'done' ? `Done · Day ${stop.day ?? '·'}` : `Stop ${n}`}</span>
        </div>
        {printOf(stop) === 'developing' && <p className="signpost-line">Your crew are developing its picture. It will be dry soon.</p>}
        {builds && <p className="signpost-line">Builds: <b>{builds.toLowerCase()}</b></p>}
        {gives && <p className="signpost-line">Gives your film: <b>{gives}</b></p>}
        <div className="row" style={{ gap: 12, marginTop: 6 }}>
          <button type="button" className="btn" style={{ flex: 1 }} onClick={() => onStart(c, stop.state === 'next' ? 'next' : stop.state === 'done' ? 'again' : 'pick')}>{stop.state === 'done' ? 'Do it again' : 'Start'}</button>
          <button type="button" className="link" onClick={onClose}>Not today</button>
        </div>
      </div>
    </div>
  );
}

/** Which chapter of the road is on screen, for the header: ‹ Chapter 1 of 3 ›. */
export function ChapterPager({ n, of, onChapter }: { n: number; of: number; onChapter: (n: number) => void }) {
  if (of <= 1) return <span className="road-pager"><span>Chapter {n}</span></span>;
  return (
    <span className="road-pager">
      <button type="button" disabled={n <= 1} onClick={() => onChapter(n - 1)} aria-label="Earlier chapter">‹</button>
      <span>Ch. {n} of {of}</span>
      <button type="button" disabled={n >= of} onClick={() => onChapter(n + 1)} aria-label="Next chapter">›</button>
    </span>
  );
}
